import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	measureShoulderDistance,
	repsValgusScore,
	repKneeSymmetryScore,
	repsHipShiftScore,
	repsTrunkCollapseScore,
	isWebview,
	getThighValgusAngle,
	appendAvg,
	arrAvg,
	segmentDataByHealthiness,
	calfSideAngle,
	torsoSideAngle,
	smooth,
	confidentSide,
	kneeForwardFlexion,
	frontHipShift,
	interpolateMidpoints,
	hipPosition,
	roundDecimal,
	normalizeX,
	repConsistency,
	tempoConsistency,
	maxDepthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	nearest
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setLogViewer,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import Leaderboard from './Leaderboard'
import KeypointLogViewer from './KeypointLogViewer'
import testData from './squat-front-side.json'

const SCORE_THRESHOLD = 0.35

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
}

const SummaryTable = ({data})=>{

	const dispatch = useDispatch()

	const maxRows = [
		{
			metric:'Max Depth (inches)',
			unit:'inch',
			values:appendAvg(data.map(d=>d.side.maxDepth.inches)).map(d=>({
				text:d.toFixed(1)
			})),
		},{
			metric:'Max Depth (percent)',
			unit:'percent',
			values:appendAvg(data.map(d=>d.side.maxDepth.percent)).map(d=>({
				text:d.toFixed(1)
			})),
		},{
			metric:'Max Hip Shift',
			unit:'inch',
			values:appendAvg(data.map(d=>d.front.hipShiftMax)).map(d=>({
				text:(
					(d>=0.099&&`left ${Math.abs(d).toFixed(1)}`) ||
					(d<=-0.099&&`right ${Math.abs(d).toFixed(1)}`) ||
					Math.abs(d).toFixed(1)
				),
				color:d<=2?'green':'red'
			}))
		},{
			metric:'Max Left Thigh Valgus Angle',
			unit:'degree',
			values:appendAvg(data.map(d=>d.front.leftValgusMax.angle)).map(d=>({
				text:(
					(d>0&&`varus ${Math.abs(d).toFixed(1)}`) ||
					(d<=-0.1&&`valgus ${Math.abs(d).toFixed(1)}`) ||
					Math.abs(d).toFixed(1)
				),
				color:d>=0?'green':'red'
			}))
		},{
			metric:'Max Right Thigh Valgus Angle',
			unit:'degree',
			values:appendAvg(data.map(d=>d.front.rightValgusMax.angle)).map(d=>({
				text:(
					(d>0&&`varus ${Math.abs(d).toFixed(1)}`) ||
					(d<-0.01&&`valgus ${Math.abs(d).toFixed(1)}`) ||
					Math.abs(d).toFixed(1)
				),
				color:d>=0?'green':'red'
			}))
		},{
			metric:'Max Knee Symmetry Difference (degrees)',
			unit:'degree',
			values:appendAvg(data.map(d=>d.front.kneeSymmetryDifferenceMax.angle)).map(d=>({
				text:d.toFixed(1),
				color:d<=12?'green':'red'
			}))
		},{
			metric:'Max Left Knee Inward Distance',
			unit:'inch',
			values:appendAvg(data.map(d=>d.front.leftValgusMax.inches)).map(d=>({
				text:(
					(d>0&&`medial ${Math.abs(d).toFixed(1)}`) ||
					(d<=-0.1&&`lateral ${Math.abs(d).toFixed(1)}`) ||
					Math.abs(d).toFixed(1)
				),
				color:d<=0?'green':'red'
			}))
		},{
			metric:'Max Right Knee Inward Distance',
			unit:'inch',
			values:appendAvg(data.map(d=>d.front.rightValgusMax.inches)).map(d=>({
				text:(
					(d>0&&`medial ${Math.abs(d).toFixed(1)}`) ||
					(d<-0.01&&`lateral ${Math.abs(d).toFixed(1)}`) ||
					Math.abs(d).toFixed(1)
				),
				color:d<=0?'green':'red'
			}))
		},{
			metric:'Max Knee Symmetry Difference (inches)',
			unit:'inch',
			values:appendAvg(data.map(d=>d.front.kneeSymmetryDifferenceMax.inches)).map(d=>({
				text:d.toFixed(1),
				color:d<=2?'green':'red'
			}))
		},{
			metric:'Max Knee Fwd. Flexion',
			unit:'inch',
			values:appendAvg(data.map(d=>d.side.kneeForwardFlexionMax)).map(d=>({
				text:d.toFixed(1)
			}))
		},{
			metric:'Min Trunk Angle',
			unit:'degree',
			values:appendAvg(data.map(d=>d.side.torsoAngleMin)).map(d=>({
				text:d.toFixed(1)
			}))
		},{
			metric:'Min Shin Angle',
			unit:'degree',
			values:appendAvg(data.map(d=>d.side.calfAngleMin)).map(d=>({
				text:d.toFixed(1)
			}))
		},{
			metric:'Max Trunk-Shin Angle Diff.',
			unit:'degree',
			values:appendAvg(data.map(d=>d.side.trunkCalfAngleDiffMax)).map(d=>({
				text:d.toFixed(1),
				color:d<=10?'green':'red'
			}))
		}
	]

	const unitSymbol = unit => {
		switch(unit){
			case 'inch': return '"'
			case 'degree': return <>&#xb0;</>
			case 'percent': return '%'
		}
	}

	return (
		<div className="mb-3">
			<Table size="sm" responsive striped bordered>
				<thead>
					<tr>
						<th>Metric</th>
						{data.map((_,i)=>(
							<th className="text-right">Rep {i+1}</th>
						))}
						<th className="text-right">Average</th>
					</tr>
				</thead>
				<tbody>
					{maxRows.map(row=>(
						<tr>
							<td>{row.metric}</td>
							{row.values.map(v=>(
								<td align="right" style={{
									fontFamily:'monospace',
									color: v.color || '#000'
								}}>
									{v.text}
									{unitSymbol(row.unit)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</Table>
		</div>
	)
}

const IssuesFound = ({data})=>{

	const dispatch = useDispatch()

	const appendAvg = arr => [...arr,arrAvg(arr)]

	const unhealthyForm = data.map( d => {
		const result = []
		const hipShifts = d.front.hipShiftsSegments.filter(s=>!s.healthy)
		if (hipShifts.length>0) {
			const issues = hipShifts.map(s=>(
				{
					issue:'Large Hip Shift',
					start:{
						depth:d.front.depths[s.i1],
						logIdx:s.logIdx1,
						t:s.t1
					},
					end:{
						depth:d.front.depths[s.i2],
						logIdx:s.logIdx2,
						t:s.t2
					}
				}
			))
			result.push(...issues)
		}
		const leftValgus = d.front.leftValgusSegments.filter(s=>!s.healthy)
		if (leftValgus.length>0) {
			const issues = leftValgus.map(v=>(
				{
					issue:'Left Knee Buckling Inward',
					start:{
						depth:d.front.depths[v.i1],
						logIdx:v.logIdx1,
						t:v.t1
					},
					end:{
						depth:d.front.depths[v.i2],
						logIdx:v.logIdx2,
						t:v.t2
					}
				}
			))
			result.push(...issues)
		}
		const rightValgus = d.front.rightValgusSegments.filter(s=>!s.healthy)
		if (rightValgus.length>0) {
			const issues = rightValgus.map(v=>(
				{
					issue:'Right Knee Buckling Inward',
					start:{
						depth:d.front.depths[v.i1],
						logIdx:v.logIdx1,
						t:v.t1
					},
					end:{
						depth:d.front.depths[v.i2],
						logIdx:v.logIdx2,
						t:v.t2
					}
				}
			))
			result.push(...issues)
		}
		const kneeSymmetryDifferences = d.front.kneeSymmetryDifferenceSegments.filter(s=>!s.healthy)
		if (kneeSymmetryDifferences.length>0) {
			const issues = kneeSymmetryDifferences.map(k=>(
				{
					issue:'Large Knee Movement Asymmetry',
					start:{
						depth:d.front.depths[k.i1]*69,
						logIdx:k.logIdx1,
						t:k.t1
					},
					end:{
						depth:d.front.depths[k.i2]*69,
						logIdx:k.logIdx2,
						t:k.t2
					}
				}
			))
			result.push(...issues)
		}
		const trunkCalfAngleDiffs = d.side.trunkCalfAngleDiffSegments.filter(s=>!s.healthy)
		if (trunkCalfAngleDiffs.length>0) {
			const issues = trunkCalfAngleDiffs.map(t=>(
				{
					issue:'Trunk Collapsing Forward',
					start:{
						depth:d.side.depths[t.i1]*69,
						logIdx:t.logIdx1,
						t:t.t1
					},
					end:{
						depth:d.side.depths[t.i2]*69,
						logIdx:t.logIdx2,
						t:t.t2
					}
				}
			))
			result.push(...issues)
		}
		return result
	})

	return (
		<div className="mb-3">
			<div>
				<div className="text-center">
					<strong className="d-block">Issues Found Per Rep</strong>
					<small className="d-block">
						Click on any of the issues to jump to that point in the preview below
					</small>
				</div>
				<div className="d-flex justify-content-center flex-wrap">
					{unhealthyForm.map((issues,i)=>(
						<div className="p-3" style={{width:'270px'}}>
							<div className="text-center">
								Rep {i+1}
							</div>
							<hr className="my-1"/>
							<div>
								{issues.length === 0 &&
									<small className="d-block text-center">No issues found</small>
								}
								{issues.map(issue=>(
									<small 
										style={{cursor:'pointer'}}
										onClick={()=>{
											dispatch(setLogViewer({idx:issue.start.logIdx}))
										}} 
										className="d-flex justify-content-between"
									>
										<strong>{issue.issue}:</strong>
										<table>
											<thead>
												<tr className="text-muted">
													<th style={{width:'48px'}}>Start</th>
													<th style={{width:'48px'}} className="text-right">End</th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="pr-3">{issue.start.depth.toFixed(1)}"</td>
													<td className="text-right">{issue.end.depth.toFixed(1)}"</td>
												</tr>
											</tbody>
										</table>
									</small>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

const SquatDemo = ()=>{
	const dispatch = useDispatch()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const [windowWidth,windowHeight] = useWindowSize()
	const ratio = 1

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const goalCount = 5
	//which view we are scoring (ie front/side)
	const viewRef = useRef('front')
	const countRef = useRef({
		front:0,
		side:0
	})
	const countSpanRef = {
		front:useRef(null),
		side:useRef(null)
	}

	const [timeLeft, {start:startCoundown,reset:resetCountdown}] = useCountDown(3000, 1000)
	const countingDownRef = useRef(false)

	const initializeGuides = async ()=>{
		
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
		if (poses.length === 0) return
		const keypoints = poses[0].keypoints
		//using left hand for now
		resultsRef.current = emptyResults()
		countRef.current.front = 0
		squatStateRef.current = 'standing'
		countSpanRef.front.current.innerText = countRef.current.front
		squatStateSpanRef.current.innerText = stateDisplayTextMap['standing']
		//a full squat is roughly at or below: height - thigh length
		const bottomY = poseHeight(keypoints) - poseFemurLength(keypoints)*1.6 + hipPosition(keypoints).y
		guidesRef.current = {
			height:poseHeight(keypoints),
			topY:hipPosition(keypoints).y+poseHeight(keypoints)*.06,
			center:{
				left:320-(hipPosition(keypoints).x-3),
				right:320-(hipPosition(keypoints).x+3)
			},
			leftKneeX:320-keypoints[13].x,
			rightKneeX:320-keypoints[14].x,
			bottomY:Math.max(
				Math.min(400,bottomY),
				hipPosition(keypoints).y+20
			),
			shoulderDistance:measureShoulderDistance(keypoints),
			femurLength:poseFemurLength(keypoints)
		}
		setShowGuides(true)
	}

	useEffect(()=>{
		if(!overlayRef.current||!countingDownRef.current) return
		if(timeLeft===0&&countingDownRef.current){
			countingDownRef.current = false
			initializeGuides()
			overlayRef.current.innerText = 'GO!'
			setTimeout(()=>{setShowOverlay(false)},1500)
		}else{
			overlayRef.current.innerText = Math.ceil(timeLeft/1000)
		}
	},[timeLeft,countingDownRef])

	//stores results from squat analysis
	const emptyResults = ()=>({
		//log for all poses/keypoints during assessment
		keypointLog:[],
		front:{
			reps:{
				started:[],
				ended:[]
			},
			kneeCaveMap:{
				left:[],
				right:[]
			},
			hipPosition:[],
			leftKneePosition:[],
			rightKneePosition:[]
		},
		side:{
			reps:{
				started:[],
				ended:[]
			},
			keypointLog:[]
		}
	})

	const [results,setResults] = useState(testData)//useState(emptyResults())//
	const resultsRef = useRef(null)

	//console.log(results)
	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef({
		"height": 283.55948958396914,
		"femurLength": 284*.30,
		"topY": 163.00746942567827,
		"center": {
			"left": 146.91523838043213,
			"right": 140.91523838043213
		},
		"leftKneeX": 123.69457244873047,
		"rightKneeX": 162.73795127868652,
		"bottomY": 330.51472265514815,
		"shoulderDistance": 283*.15
	})

	const emailRef = useRef()
	const nameRef = useRef()

	useEffect( async ()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		standing:'Standing',
		squatting:'Squatting',
		waiting:'Waiting',
		setup:'Get Ready'
	}

	const initState = 'waiting'
	const squatStateRef = useRef(initState)
	const squatStateSpanRef = useRef(null)
	const resetRef = useRef(false)

	//predicates
	const isFrontView = () => viewRef.current === 'front'
	const isSideView = () => viewRef.current === 'side'
	const shouldReset = () => resetRef.current

	//functions (getters,setters,effects)
	const getCurrentView = ()=> viewRef.current
	const consumeReset = ()=> resetRef.current = false

	const setTemporaryOverlay = (text,ttl)=>{
		overlayRef.current.innerText = text
		setShowOverlay(true)
		setTimeout(()=>{
			setShowOverlay(false)
		},ttl)
	}

	//delay the end so we get the last bit of the final squat
	const [isDelaying,setIsDelaying] = useState(false)

	const delayFunction = (cb,delay)=>{
		if(isDelaying) return 
		setIsDelaying(true)
		setTimeout(cb,delay)
	}

	const infer = useCallback( async () => {
		//console.log(store.getState())
		//map from current state to trigger for transitioning to next
		//state
		const onTransition = (t)=>{
			if(!squatStateSpanRef.current) return
			squatStateSpanRef.current.innerText = stateDisplayTextMap[squatStateRef.current]
		}
		
		//runs on every inference to log data continuously (eg nose & knee positions)
		const squatDataLogger = (keypoints,t)=>{
			const leftKnee = keypoints[13]
			const rightKnee = keypoints[14]
			resultsRef.current.keypointLog.push({keypoints,t})
			if(isFrontView()){
				resultsRef.current.front.hipPosition.push({
					x:320 - hipPosition(keypoints).x,
					y:hipPosition(keypoints).y,
					status:calculateCenterStatus(
						320 - hipPosition(keypoints).x,
						guidesRef.current
					),
					t
				})
				resultsRef.current.front.leftKneePosition.push({
					x:Math.abs(leftKnee.x - guidesRef.current.leftKneeX),
					y:leftKnee.y,
					t
				})
				resultsRef.current.front.rightKneePosition.push({
					x:Math.abs(rightKnee.x - guidesRef.current.rightKneeX),
					y:rightKnee.y,
					t
				})
			}

			if (isSideView()) {
				resultsRef.current.side.keypointLog.push(
					{keypoints,t}
				)
			}
		}

		//These are triggers that get fired if in the current state
		//For example: if the current state is standing, then only the 
		//trigger for standing gets fired 
		const squatTransitionTriggers = {
			setup:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//left arm keypoints
				const lWrist = keypoints[10]
				const lElbow = keypoints[8]
				const lShoulder = keypoints[6]

				const lArmUp = (
					lWrist.y < lShoulder.y &&
					lElbow.y < lShoulder.y
				)

				//right arm keypoints
				const rWrist = keypoints[9]
				const rElbow = keypoints[7]
				const rShoulder = keypoints[5]

				const rArmUp = (
					rWrist.y < rShoulder.y &&
					rElbow.y < rShoulder.y
				)

				if(lArmUp&&rArmUp&&!countingDownRef.current){
					startCoundown()
					countingDownRef.current = true
					setShowOverlay(true)
				}
			},
			standing:(keypoints,t)=>{
				try {
					//if person is fully facing sideways reset the topY value
					//reset the topY so we don't get false reps if
					//the person shifts around
					if (
						measureShoulderDistance(keypoints)<guidesRef.current.shoulderDistance*.25
						&&
						shouldReset()
					) {
						consumeReset()
						guidesRef.current.topY = (
							hipPosition(keypoints).y+poseHeight(keypoints)*.07
						)
					}

					//if in standing state transition to squating 
					//and log the point that the rep started
					if ( hipPosition(keypoints).y >= guidesRef.current.topY ) {
						squatStateRef.current = 'squatting'
						const t =  Date.now()
						resultsRef.current[getCurrentView()].reps.started.push(t)
					}
				} catch (err) {
					console.error(err)
				}
			},
			squatting:(keypoints,t)=>{
				if ( hipPosition(keypoints).y <= guidesRef.current.topY ) {
					squatStateRef.current = 'standing'
					countRef.current[viewRef.current] += 1
					countSpanRef[getCurrentView()].current.innerText = countRef.current[viewRef.current]
					resultsRef.current[getCurrentView()].reps.ended.push(t)
					//transition to side view when front view hasd 5 squat count
					if (isFrontView() && countRef.current.front === goalCount) {
						setTemporaryOverlay('Turn Left',1000)
						//set topY to very high value so that we don't count false
						//reps while they are turning
						guidesRef.current.topY = 9999
						resetRef.current = true
						viewRef.current = 'side'
						resultsRef.current.front.kneeCaveMap.left = getKneeCaveTimeMap(
							resultsRef.current.front.reps,
							resultsRef.current.front.leftKneePosition,
							resultsRef.current.front.hipPosition
						)
						resultsRef.current.front.kneeCaveMap.right = getKneeCaveTimeMap(
							resultsRef.current.front.reps,
							resultsRef.current.front.rightKneePosition,
							resultsRef.current.front.hipPosition
						)
					}
					//if sideview has 5 squats then the assessment is finished
					if ( isSideView() && countRef.current.side === goalCount ) {
						setTemporaryOverlay('Done. Processing Results',2000)
						squatStateRef.current = 'done'
					}
				}
			},
			done:()=>{
				delayFunction(()=>{
					cancelInferencing()
					setShowGuides(false)
					setResults(resultsRef.current)
					setShowResults(true)
					//testVidRef.current.pause()
					trackResultsRef.current = false
					//testVidRef.current.currentTime = 0
				},2000)
			}
		}

		if (!webcamRef.current||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				//console.log('Not initialized')
				return
			}
			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				posesRef.current = poses
				if ( poses.length > 0 ) {
					const keypoints = poses[0].keypoints
					const nose = keypoints[0]
					const t = Date.now()
					if ( nose.score >= SCORE_THRESHOLD ) {
						if(squatStateRef.current!=='setup') squatDataLogger(keypoints,t) 
						const transitionTrigger = squatTransitionTriggers[squatStateRef.current]
						if (transitionTrigger) {
							transitionTrigger(keypoints,t)
							onTransition(t)
						}
					}
				}
			} catch(err) {
				console.log(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)

	const startDemo = async ()=>{
		viewRef.current = 'front'
		setStarted(true)
		cancelInferencing()
		resetCountdown()
		setShowGuides(false)
		//put log viewer idx back to zero to prevent
		//index out of bounds errr
		dispatch(
			setLogViewer({idx:0})
		)
		resultsRef.current = emptyResults()
		countRef.current = {front:0,side:0}
		countSpanRef.front.current.innerText = countRef.current.front
		countSpanRef.side.current.innerText = countRef.current.side
		squatStateSpanRef.current.innerText = stateDisplayTextMap['setup']

		initializedRef.current = true
		squatStateRef.current = 'setup'
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		//testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false) 
		setShowGuides(false)
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		countingDownRef.current = false
		resultsRef.current = emptyResults()
		countRef.current = {front:0,side:0}
		viewRef.current = 'front'
	}

	const stopDemo = ()=>{
		viewRef.current = 'front'
		setShowOverlay(false)
		setShowGuides(false)
		countingDownRef.current = false
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		resultsRef.current = emptyResults()
		countRef.current = {front:0,side:0}
		countSpanRef.front.current.innerText = countRef.current.front
		countSpanRef.side.current.innerText = countRef.current.side
		squatStateSpanRef.current.innerText = stateDisplayTextMap[initState]
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleSave = ()=>{
		setSaving(true)
		const data = processResultsData(results)
		const email = emailRef.current.value
		const name = nameRef.current.value

		dispatch(addLeaderboardRecord({
			contact_email:email,
			depth_score:maxDepthScore(
				arrAvg(data.map(d=>d.side.maxDepth.percent)),
				guidesRef.current.height,
				guidesRef.current.femurLength
			).toFixed(1),
			trunk_collapse_score:repsTrunkCollapseScore(
				results.side.reps,
				results.keypointLog
			).toFixed(1),
			hip_shift_score:repsHipShiftScore(
				results.front.reps,
				results.front.hipPosition
			).toFixed(1),
			left_valgus_score:repsValgusScore(
				results.side.reps,
				results.keypointLog,
				'left'
			).toFixed(1),
			right_valgus_score:repsValgusScore(
				results.side.reps,
				results.keypointLog,
				'right'
			).toFixed(1),
			knee_symmetry_score:repKneeSymmetryScore(
				results.front.reps,
				results.keypointLog,
				guidesRef.current.height,
			).toFixed(1),
			average_score:averageScore().toFixed(1),
			name:name,
		})).then(()=>{
			setSaving(false)
			nameRef.current.value = ''
			emailRef.current.value = ''
		})
	}

	const handleAddToProgress = ()=>{
		const data = processResultsData(results)
		const metrics = [
			{
				name:'Max Depth (inches)',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.side.maxDepth.inches)).toFixed(1)
			},{
				name:'Max Depth (percent)',
				description:'',
				//percent
				unit_id:7,
				value:arrAvg(data.map(d=>d.side.maxDepth.percent)).toFixed(1)
			},{
				name:'Max Hip Shift',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.front.hipShiftMax)).toFixed(1)
			},{
				name:'Max Left Thigh Valgus Angle',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.front.leftValgusMax.angle)).toFixed(1)
			},{
				name:'Max Right Thigh Valgus Angle',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.front.rightValgusMax.angle)).toFixed(1)
			},{
				name:'Max Knee Symmetry Difference (degrees)',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.front.kneeSymmetryDifferenceMax.angle)).toFixed(1)
			},{
				name:'Max Left Knee Inward Distance',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.front.leftValgusMax.inches)).toFixed(1)
			},{
				name:'Max Right Knee Inward Distance',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.front.rightValgusMax.inches)).toFixed(1)
			},{
				name:'Max Knee Symmetry Difference (inches)',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.front.kneeSymmetryDifferenceMax.inches)).toFixed(1)
			},{
				name:'Max Knee Fwd. Flexion',
				description:'',
				//inches
				unit_id:6,
				value:arrAvg(data.map(d=>d.side.kneeForwardFlexionMax)).toFixed(1)
			},{
				name:'Min Trunk Angle',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.side.torsoAngleMin)).toFixed(1)
			},{
				name:'Min Leg Angle',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.side.calfAngleMin)).toFixed(1)
			},{
				name:'Max Trunk-Shin Angle Diff.',
				description:'',
				//degrees
				unit_id:10,
				value:arrAvg(data.map(d=>d.side.trunkCalfAngleDiffMax)).toFixed(1)
			}
		]

		dispatch(setMetricCreator({
			show:true,
			category:'AI Squat Assessment',
			date:dateToYMD(new Date()),
			aiLog:JSON.stringify(results),
			metrics
		}))
	}

	const averageScore = ()=>{
		const data = processResultsData(results)
		const scores = [
			maxDepthScore(
				arrAvg(data.map(d=>d.side.maxDepth.percent)),
				guidesRef.current.height,
				guidesRef.current.femurLength
			),
			repsTrunkCollapseScore(
				results.side.reps,
				results.keypointLog
			),
			repsHipShiftScore(
				results.front.reps,
				results.front.hipPosition
			),
			repsValgusScore(
				results.side.reps,
				results.keypointLog,
				'left'
			),
			repsValgusScore(
				results.side.reps,
				results.keypointLog,
				'right'
			),
			repKneeSymmetryScore(
				results.front.reps,
				results.keypointLog,
				guidesRef.current.height,
			)
		]
		const average = scores.reduce(
			(carry,score) => carry + score,
			0
		)/scores.length
		return average
	}

	const processResultsData = (data) => {
		const numInterpolationPasses = 2
		const baselineSmoothRadius = 10
		const interpolatedKeypointLog = interpolateMidpoints(
			data.keypointLog,
			numInterpolationPasses
		)

		const smoothedFacingSideLog = smooth(
			interpolatedKeypointLog.map( d => confidentSide(d.keypoints) ),
			baselineSmoothRadius*(1+numInterpolationPasses)
		)

		//map from time to side
		const timeSideMap = {}
		const timeIdxMap = {}
		for (var i = 0; i < interpolatedKeypointLog.length; i++) {
			const t = interpolatedKeypointLog[i].t
			const side = smoothedFacingSideLog[i]
			timeSideMap[t] = side
			timeIdxMap[t] = i
		}

		const startPose = interpolatedKeypointLog[0].keypoints
		const height = poseHeight(startPose)
		const hipStartY = hipPosition(startPose).y

		const result = []
		//map from rep number to data specific to that rep
		const repMap = {}
		const views = ['front','side']
		//loop over each view and process the reps
		views.forEach(view=>{
			//extract rep slice data
			const repSlices = extractRepSlices(
				data[view].reps,
				interpolatedKeypointLog
			)
			//loop over each rep, and build the repMap of key data
			repSlices.forEach((repSlice,i)=>{
				//initialize rep data 
				if (!repMap[i]) repMap[i] = {}
				//initialize the view data
				if (!repMap[i][view]) repMap[i][view] = {}
				const times = repSlice.map( p => p.t )
				const depths = repSlice.map(
					pose => (
						Math.abs(hipStartY - hipPosition(pose.keypoints).y)/height
					)
				)
				repMap[i][view].depths = depths
				//ratio
				const maxDepth = Math.max(...depths)
				repMap[i][view].maxDepth = {
					percent:maxDepth*100,
					inches:maxDepth*69
				}
				//look for unhealthy depth ranges for each view
				if ( view === 'front' ) {
					//get hip shift data
					const hipShifts = repSlice.map(
						pose => (
							{
								shift:frontHipShift(pose.keypoints,height),
								logIdx:timeIdxMap[pose.t],
								t:pose.t
							}
						)
					)

					repMap[i].front.hipShifts  = hipShifts
					repMap[i].front.hipShiftMax = Math.max(...hipShifts.map(p=>p.shift))
					repMap[i].front.hipShiftsSegments  = segmentDataByHealthiness(
						hipShifts,
						'shift',
						v=>v<=2
					)


					const leftValgusData = repSlice.map(pose=>{
						const toeOffset = 1.75/69*height
						const knee = pose.keypoints[13]
						const ankle = pose.keypoints[15]
						const toeX = ankle.x - toeOffset
						const inches = (toeX - knee.x)/height*69
						const angle = getThighValgusAngle('left',pose.keypoints)
						return {valgus:{inches,angle},t:pose.t,logIdx:timeIdxMap[pose.t]}
					})//end repslice.map	

					repMap[i].front.leftValgusData  = leftValgusData
					repMap[i].front.leftValgusMax = {
						inches:Math.max(...leftValgusData.map(p=>p.valgus.inches)),
						angle:Math.min(...leftValgusData.map(p=>p.valgus.angle))
					}
					repMap[i].front.leftValgusSegments  = segmentDataByHealthiness(
						leftValgusData.map(v=>({
							...v,
							valgus:v.valgus.inches
						})),
						'valgus',
						v=>v<=0
					)

					const rightValgusData = repSlice.map(pose=>{
						const toeOffset = 1.75/69*height
						const knee = pose.keypoints[14]
						const ankle = pose.keypoints[16]
						const toeX = ankle.x + toeOffset
						const inches = (knee.x - toeX)/height*69
						const angle = getThighValgusAngle('right',pose.keypoints)
						return {valgus:{inches,angle},t:pose.t,logIdx:timeIdxMap[pose.t]}
					})//end repslice.map

					repMap[i].front.rightValgusData  = rightValgusData
					repMap[i].front.rightValgusMax = {
						inches:Math.max(...rightValgusData.map(p=>p.valgus.inches)),
						angle:Math.min(...rightValgusData.map(p=>p.valgus.angle))
					}
					repMap[i].front.rightValgusSegments  = segmentDataByHealthiness(
						rightValgusData.map(v=>({
							...v,
							valgus:v.valgus.inches
						})),
						'valgus',
						v=>v<=0
					)

					const kneeSymmetryDifferences = leftValgusData.map((leftValgus,i)=>(
						{
							t:leftValgus.t,
							logIdx:timeIdxMap[leftValgus.t],
							diff:{
								inches:Math.abs(leftValgus.valgus.inches - rightValgusData[i].valgus.inches),
								angle:Math.abs(leftValgus.valgus.angle - rightValgusData[i].valgus.angle)
							}
						}
					))

					repMap[i].front.kneeSymmetryDifferences  = kneeSymmetryDifferences
					repMap[i].front.kneeSymmetryDifferenceMax = {
						inches:Math.max(...kneeSymmetryDifferences.map(p=>p.diff.inches)),
						angle:Math.max(...kneeSymmetryDifferences.map(p=>p.diff.angle))
					}
					repMap[i].front.kneeSymmetryDifferenceSegments  = segmentDataByHealthiness(
						kneeSymmetryDifferences.map(d=>({
							...d,
							diff:d.diff.inches
						})),
						'diff',
						v=>v<=2
					)
					
				}//end if front view
				if (view === 'side') {
					//calculate knee forward flexion values
					const kneeFwdFlexions = repSlice.map(
						pose => (
							{
								forwardFlexion:kneeForwardFlexion(pose.keypoints,timeSideMap[pose.t],height),
								logIdx:timeIdxMap[pose.t],
								t:pose.t
							}
						)
					)

					repMap[i].side.kneeForwardFlexions  = kneeFwdFlexions
					repMap[i].side.kneeForwardFlexionMax = Math.max(...kneeFwdFlexions.map(p=>p.forwardFlexion))
					repMap[i].side.kneeForwardFlexionSegemnts  = segmentDataByHealthiness(
						kneeFwdFlexions,
						'forwardFlexion',
						v=>true
					)

					//get min trunk angle values
					const torsoAngles = repSlice.map(
						pose => (
							{
								angle:torsoSideAngle(pose.keypoints,timeSideMap[pose.t]),
								logIdx:timeIdxMap[pose.t],
								t:pose.t
							}
						)
					)

					repMap[i].side.torsoAngleMin = Math.min(...torsoAngles.map(p=>p.angle))

					repMap[i].side.torsoAngles  = torsoAngles

					

					//get calf angles
					const calfAngles = repSlice.map(
						pose => (
							{
								angle:calfSideAngle(pose.keypoints,timeSideMap[pose.t]),
								logIdx:timeIdxMap[pose.t],
								t:pose.t
							}
						)
					)
					repMap[i].side.calfAngleMin = Math.min(...calfAngles.map(p=>p.angle))

					repMap[i].side.calfAngles  = calfAngles

					//trunk/calf angle diff
					const trunkCalfAngleDiffs = calfAngles.map((calfAngle,i)=>(
						{
							diff:calfAngle.angle - torsoAngles[i].angle,
							logIdx:timeIdxMap[calfAngle.t],
							t:calfAngle.t
						}
					))

					repMap[i].side.trunkCalfAngleDiffs  = trunkCalfAngleDiffs
					repMap[i].side.trunkCalfAngleDiffMax= Math.max(...trunkCalfAngleDiffs.map(p=>p.diff))
					repMap[i].side.trunkCalfAngleDiffSegments  = segmentDataByHealthiness(
						trunkCalfAngleDiffs,
						'diff',
						v=>v<=10
					)

				}//end if side view
			})//end repSlices.forEach
		})//end views.forEach
		return Object.values(repMap)
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.2)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'120px',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{position:'relative'}}>
						
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							width: "100vw",
							height: "100vh",
						}}>
							{showGuides&&<Guides 
								guides={guidesRef.current} 
								ratio={1}
								scale={webcamRef.current.video.getBoundingClientRect().width/320}
							/>}
						</svg>
					</div>
					
					{/*<video 
						style={{
							height:`${windowHeight}px`,
							width:`${windowWidth}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/out.mp4"
					/>*/}
					<Webcam 
						style={{
							height:windowWidth>windowHeight?'100vh':'auto',
							width:windowWidth>=windowHeight?'auto':'100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={320} height={320} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-around">
							<div className="text-center">
								<div style={{fontSize:'40px'}}>
									<span ref={countSpanRef.front}>{countRef.current.front}</span>/{goalCount}
								</div>
								<div>
									<small className="text-muted">Front</small>
								</div>
							</div>
							<div style={{background:'#aaa',width:'1px'}}/>
							<div className="text-center">
								<div style={{fontSize:'40px'}}>
									<span ref={countSpanRef.side}>{countRef.current.side}</span>/{goalCount}
								</div>
								<div>
									<small className="text-muted">Side</small>
								</div>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={squatStateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px',
					paddingBottom:'100px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<SummaryTable 
						data={processResultsData(results,guidesRef.current)}
					/>
					<div className="text-right">
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						<Button 
							onClick={handleAddToProgress}
							variant="outline-success" 
							className="px-5"
						>
							Add to {isClient?'':"Client's"} Progress Metrics
						</Button>
					</div>
					<IssuesFound 
						data={processResultsData(results,guidesRef.current)}
					/>
					<KeypointLogViewer 
						id="squat"
						demo="squat"
						results={results}
					/>
					<div style={{width:'100%',height:'200px'}}>
						<Line
							options={{
								maintainAspectRatio:false,
								responsive:true,
								animation: {
									duration: 0
								},
								hover: {
									mode: 'nearest'
								},
								scales: {
									xAxes: [{
										ticks:{
											callback:(label)=>(label/1000).toFixed(1),
											maxTicksLimit:10
										},
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Time (seconds)'
										}
									}],//end xAxes
									yAxes: [{
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Height'
										}
									}]//end yAxes
								}//end scales
							}}
							data={
								{
									labels:results.front.hipPosition.map((p)=>p.t - results.front.hipPosition[0].t),
									datasets: [
										{
											lineTension: 0,
											label: 'Height',
											data:results.front.hipPosition.map(p=>({
												x:p.t - results.front.hipPosition[0].t,
												y:roundDecimal(guidesRef.current.topY - p.y + guidesRef.current.height),
												status:p.status
											})),
											backgroundColor: "rgba(75,192,192,0.2)",
											pointBackgroundColor: function(context) {
												const index = context.dataIndex
												const value = context.dataset.data[index]
												return (
													value.status!=='center'
													? "rgba(255,50,50,1)"
													: "transparent"
												)
											},
											borderColor: "rgba(73, 197, 235,1)"
										},{
											showLine:false,
											lineTension: 0,
											label: 'Rep Started',
											fill:false,
											data:results.front.reps.started.map(t=>({
												x:t - results.front.hipPosition[0].t,
												y:roundDecimal(guidesRef.current.height + 20)
											})),
											borderColor: "rgba(52, 168, 114,1)",
											backgroundColor:'rgba(52, 168, 114,0.2)'
										},{
											showLine:false,
											lineTension: 0,
											label: 'Rep Finished',
											fill:false,
											data:results.front.reps.ended.map(t=>({
												x:t - results.front.hipPosition[0].t,
												y:roundDecimal(guidesRef.current.height + 20)
											})),
											borderColor: "rgba(230, 137, 16,1)",
											backgroundColor:'rgba(230, 137, 16,0.2)'
										}
									]
								}
							}
						/>
					</div>
					<div style={{width:'100%',height:'200px'}}>
						<Line
							options={{
								maintainAspectRatio:false,
								responsive:true,
								animation: {
									duration: 0
								},
								scales: {
									xAxes: [{
										ticks:{
											callback:(label)=>(label/1000).toFixed(1),
											maxTicksLimit:10
										},
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Time (seconds)'
										}
									}],//end xAxes
									yAxes: [{
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Knee Center Deviation'
										}
									}]//end yAxes
								}//end scales
							}}
							data={
								{
									labels:results.front.hipPosition.map((p)=>p.t - results.front.hipPosition[0].t),
									datasets: [
										{
											lineTension: 0,
											label: 'Left Knee',
											data:normalizeX(results.front.leftKneePosition).map(p=>({
												x:p.t ,
												y:roundDecimal(p.x)
											})),
											pointBackgroundColor:function(context){
												const index = context.dataIndex
												const pt = context.dataset.data[index]
												
												return (
													results.front.kneeCaveMap.left[pt.x]
													? "rgba(255,50,50,1)"
													: "transparent"
												)
												
											},
											backgroundColor: "rgba(143, 212, 123,0.2)",
											borderColor: "rgba(143, 212, 123,1)"
										},{
											lineTension: 0,
											label: 'Right Knee',
											pointBackgroundColor:function(context){
												const index = context.dataIndex
												const pt = context.dataset.data[index]
												
												return (
													results.front.kneeCaveMap.right[pt.x]
													? "rgba(255,50,50,1)"
													: "transparent"
												)
												
											},
											data:normalizeX(results.front.rightKneePosition).map(p=>({
												x:p.t ,
												y:roundDecimal(p.x)
											})),
											backgroundColor: "rgba(82, 124, 196,0.2)",
											borderColor: "rgba(82, 124, 196,1)"
										}
									]
								}
							}
						/>
					</div>
					<div className="d-flex flex-wrap justify-content-between mt-3">
						{extractRepSlices(results.front.reps,results.front.hipPosition).map((data,i)=>(
							<div style={{width:'180px',height:'500px'}}>
								<Scatter
									options={{
										maintainAspectRatio:false,
										responsive:true,
										animation: {
											duration: 0
										},
										title:{
											display:true,
											text:`Rep ${i+1} center`,
										},
										scales: {
											xAxes: [{
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Center Deviation'
												}
											}],//end xAxes
											yAxes: [{
												display:true,
												scaleLabel:{
													display:true,
													labelString:'Squat Height'
												}
											}]//end yAxes
										}//end scales
									}}
									data={{
										datasets:[
											{
												label:`Down`,
												data:normalizeX(
													data.slice(0,argMax(data.map(p=>p.y)))
												).map(p=>({x:p.x-0.5,y:roundDecimal(p.y)})),
												backgroundColor: 'rgb(3, 127, 252)'
											},{
												label:`Up`,
												data:normalizeX(
													data.slice(argMax(data.map(p=>p.y)))
												).map(p=>({x:p.x-0.5,y:roundDecimal(p.y)})),
												backgroundColor: 'rgb(127, 252, 3)'
											},
										]
									}}
								/>
							</div>
						))}
						<div 
							className="d-flex flex-column align-items-stretch pb-5" 
							style={{flex:'0 0 auto',width:'400px',maxWidth:'100%'}}
						>
							{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
								<>
									<Table striped size="sm">
										<thead>
											<tr>
												<th>Metric</th>
												<th className="text-right">Score</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td>Depth</td>
												<td className="text-right">
													{maxDepthScore(
														arrAvg(processResultsData(results).map(d=>d.side.maxDepth.percent)),
														guidesRef.current.height,
														guidesRef.current.femurLength
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>Hip Shift</td>
												<td className="text-right">
													{repsHipShiftScore(
														results.front.reps,
														results.front.hipPosition
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>Left Knee</td>
												<td className="text-right">
													{repsValgusScore(
														results.side.reps,
														results.keypointLog,
														'left'
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>Right Knee</td>
												<td className="text-right">
													{repsValgusScore(
														results.side.reps,
														results.keypointLog,
														'right'
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>Knee Movement Symmetry</td>
												<td className="text-right">
													{repKneeSymmetryScore(
														results.front.reps,
														results.keypointLog,
														guidesRef.current.height
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>Trunk-Shin Angle</td>
												<td className="text-right">
													{repsTrunkCollapseScore(
														results.side.reps,
														results.keypointLog,
													).toFixed(0)}
												</td>
											</tr>
											<tr>
												<td>
													<strong>Total Score</strong>
												</td>
												<td className="text-right">
													<strong>{averageScore().toFixed(0)}</strong>
												</td>
											</tr>
										</tbody>
									</Table>
									<strong className="mt-2">Name</strong>
									<Form.Control 
										ref={nameRef}
										placeholder="name"
									/>
									<strong>Email</strong>
									<Form.Control 
										ref={emailRef}
										placeholder="email"
									/>
									<Button
										disabled={saving}
										onClick={handleSave}
										variant="outline-success"
										className="mt-2"
										block
									>
										Add to Leaderboard {saving&&<Spinner size="sm" animation="border"/>}
									</Button>
								</>
							}
							<div className="mt-1 d-flex">
								<Button 
									className="mr-2"
									onClick={restartDemo}
									variant="outline-dark"
								>
									Restart
								</Button>
								{!isWebview()&&
									<Button 
										onClick={()=>{
											dispatch(setShow(false))
										}}
										variant="outline-danger" 
									>
										Close
									</Button>
								}
							</div>
						</div>
					</div>
				</div>
			}
			{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
				<div style={{
					position:'absolute',
					top:0,left:0,
					transform:`translateX(${showLeaderboard?'0':'-100%'})`, 
					transition:'0.2s',
					width:'800px',
					maxWidth:'100%',
					height:'100vh',
					
					zIndex:6,
					boxShadow:showLeaderboard?'0 0 16px rgba(0,0,0,0.6)':'none'
				}}>
					<button 
						onClick={()=>{
							setShowLeaderboard(p=>!p)
						}}
						style={{
							position:'absolute',
							top:'80px',right:0,
							transform:'translateX(100%)',
							padding:'16px',
							background:'#fff',
							border:'none',
							zIndex:5,
							boxShadow:'0 0 16px rgba(0,0,0,0.2)',
						}}
					>
						Leaderboard
					</button>
					<div style={{
						width:'100%',
						height:'100%',
						overflow:'auto',
						position:'absolute',
						background:'#fff',
						padding:'8px',
						top:0,left:0,
						zIndex:6
					}}>
						<Leaderboard/>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={()=>{setShowHowTo(false)}}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically score your squat form. 
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Step back until your <strong>full body is in view</strong> of the camera and make sure to <strong>face the camera straight on</strong></li>
						<li><span className="mr-2">3.</span>When ready, <strong>place your hands behind your head</strong> and a <strong>3 second countdown</strong> will appear</li>
						<li><span className="mr-2">4.</span>When you see <span className="text-success"><strong>GO!</strong></span> do <strong>5 squats facing the camera</strong></li>
						<li><span className="mr-2">5.</span><strong>Turn Left</strong> and do <strong>5 more squats</strong></li>
						<li><span className="mr-2">6.</span>View your results and have fun trying to get a better score!</li>
						<li><span className="mr-2">7.</span>If the <span id={styles.RestartHowTo}>Restart</span> button is not in view <strong>scroll down</strong></li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}

export default SquatDemo